<template>
  <div v-if="visible" class="job-progress">
    <div class="progress-overlay" @click.self="close">
      <div class="progress-modal">
        <div class="progress-header">
          <h3>{{ title }}</h3>
          <button @click="close" class="close-btn" :title="$t('editor.close')">
            ×
          </button>
        </div>
        
        <div class="progress-content">
          <div class="progress-info">
            <div class="progress-status">
              <span class="status-label">{{ $t('editor.status') }}</span>
              <span class="status-value" :class="statusClass">{{ statusText }}</span>
            </div>
            
            <div class="progress-bar-container">
              <div class="progress-bar" :style="{ width: progress + '%' }"></div>
            </div>
            
            <div class="progress-details">
              <div class="progress-percentage">{{ progress }}%</div>
              <div class="progress-message" v-if="message">{{ message }}</div>
            </div>
            
            <div class="job-id" v-if="jobId">
              {{ $t('editor.jobId') }} <code>{{ jobId }}</code>
            </div>
          </div>
          
          <div class="progress-actions" v-if="showActions">
            <button @click="close" class="btn btn-secondary" v-if="status === 'completed' || status === 'failed'">
              {{ $t('editor.close') }}
            </button>
            <button @click="retry" class="btn btn-primary" v-if="status === 'failed'">
              {{ $t('editor.retry') }}
            </button>
            <button @click="cancel" class="btn btn-secondary" v-if="status === 'processing' || status === 'pending'">
              {{ $t('editor.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { toast } from 'vue3-toastify'

export default {
  name: 'JobProgress',
  props: {
    jobId: {
      type: String,
      default: ''
    },
    jobType: {
      type: String,
      default: 'pdf',
      validator: (value) => ['pdf', 'style'].includes(value)
    },
    title: {
      type: String,
      default: 'Processing Job'
    },
    autoPoll: {
      type: Boolean,
      default: true
    },
    pollInterval: {
      type: Number,
      default: 2000
    }
  },
  emits: ['completed', 'failed', 'closed', 'retry', 'cancel'],
  setup(props, { emit }) {
    const visible = ref(false)
    const status = ref('pending')
    const progress = ref(0)
    const message = ref('')
    const error = ref(null)
    const pollTimer = ref(null)

    const statusText = computed(() => {
      const statusMap = {
        'pending': 'Pending',
        'processing': 'Processing',
        'completed': 'Completed',
        'failed': 'Failed'
      }
      return statusMap[status.value] || status.value
    })

    const statusClass = computed(() => {
      return `status-${status.value}`
    })

    const showActions = computed(() => {
      return status.value === 'completed' || status.value === 'failed' || status.value === 'processing'
    })

    const startPolling = () => {
      if (props.autoPoll && props.jobId) {
        stopPolling()
        pollTimer.value = setInterval(pollStatus, props.pollInterval)
        pollStatus() // Initial poll
      }
    }

    const stopPolling = () => {
      if (pollTimer.value) {
        clearInterval(pollTimer.value)
        pollTimer.value = null
      }
    }

    const pollStatus = async () => {
      if (!props.jobId) return
      
      try {
        const api = require('../services/api').default
        let jobStatus
        
        // Use appropriate API based on job type
        if (props.jobType === 'style') {
          jobStatus = await api.getStyleTransformJobStatus(props.jobId)
        } else {
          // Default to PDF job type for backward compatibility
          jobStatus = await api.getJobStatus(props.jobId)
        }
        
        status.value = jobStatus.status
        progress.value = jobStatus.progress || 0
        message.value = jobStatus.progressMessage || ''
        
        if (jobStatus.error) {
          error.value = jobStatus.error.message
        }
        
        // Handle completed/failed states
        if (status.value === 'completed') {
          stopPolling()
          emit('completed', jobStatus.result)
          toast.success('Job completed successfully!')
        } else if (status.value === 'failed') {
          stopPolling()
          emit('failed', error.value)
          toast.error('Job failed: ' + (error.value || 'Unknown error'))
        }
      } catch (err) {
        console.error('Failed to poll job status:', err)
        // Don't stop polling on network errors - retry next interval
      }
    }

    const show = () => {
      visible.value = true
      if (props.jobId) {
        startPolling()
      }
    }

    const close = () => {
      stopPolling()
      visible.value = false
      emit('closed')
    }

    const retry = () => {
      emit('retry')
      close()
    }

    const cancel = () => {
      emit('cancel')
      close()
    }

    // Watch for jobId changes - auto-show when jobId is provided
    watch(() => props.jobId, (newJobId) => {
      if (newJobId) {
        visible.value = true
        startPolling()
      }
    }, { immediate: true })

    // Clean up on unmount
    const cleanup = () => {
      stopPolling()
    }

    return {
      visible,
      status,
      progress,
      message,
      error,
      statusText,
      statusClass,
      showActions,
      show,
      close,
      retry,
      cancel,
      cleanup
    }
  },
  beforeUnmount() {
    this.cleanup()
  }
}
</script>

<style scoped>
.job-progress {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
}

.progress-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.progress-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: var(--gradient-hero);
  color: white;
}

.progress-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.progress-content {
  padding: 1.5rem;
}

.progress-info {
  margin-bottom: 1.5rem;
}

.progress-status {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.status-label {
  font-weight: 600;
  color: #4a5568;
  margin-right: 0.5rem;
}

.status-value {
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.9rem;
}

.status-pending {
  background: #e2e8f0;
  color: #718096;
}

.status-processing {
  background: #bee3f8;
  color: #2c5282;
}

.status-completed {
  background: #c6f6d5;
  color: #276749;
}

.status-failed {
  background: #fed7d7;
  color: #c53030;
}

.progress-bar-container {
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.75rem;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, var(--color-primary) 0%, var(--color-primary-hover) 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.progress-percentage {
  font-weight: 600;
  color: #4a5568;
  font-size: 1.1rem;
}

.progress-message {
  color: #718096;
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
  margin-left: 1rem;
}

.job-id {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.75rem;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.85rem;
  color: #718096;
  word-break: break-all;
}

.job-id code {
  background: #edf2f7;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.8rem;
}

.progress-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.2s ease;
}

.btn-primary {
  background: var(--gradient-hero);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(102, 126, 234, 0.3);
}

.btn-secondary {
  background: #e2e8f0;
  color: #4a5568;
}

.btn-secondary:hover {
  background: #cbd5e0;
}
</style>
